import { Form, Modal } from 'antd';
import { useCallback, useContext, useEffect } from 'react';

import { SalesFunnelFormContent } from '@app/components';
import { CANCEL, FRONT_WARNING, PREFIX, SAVE, SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import type {
  FunnelModalForm,
  SalesFunnelSettingsModalProps,
} from '@app/pages/SalesFunnels/types';
import { EditSalesFunnelContext } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useUpdateSalesFunnelMutation } from '@app/services/rtkQuery/SalesFunnels/api';

import type { FC } from 'react';

const { useForm } = Form;
export const SalesFunnelSettingsModal: FC<SalesFunnelSettingsModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  currentSalesFunnel,
}) => {
  const [form] = useForm();

  const { showNotification } = useNotification();
  const { refetchGetSalesFunnelStages } = useContext(EditSalesFunnelContext);

  const { activeBranchId } = useUserPrint();

  const [updateSalesFunnel, { isLoading: isModalLoading }] =
    useUpdateSalesFunnelMutation();

  useEffect(() => {
    if (currentSalesFunnel && isModalOpen) {
      form.setFieldsValue({
        Name: currentSalesFunnel.Name,
        Description: currentSalesFunnel.Description,
        UseAutoStageSwitch: currentSalesFunnel.UseAutoStageSwitch,
        SyncStagesInExistedLeads: false,
        ClientServices: currentSalesFunnel.ClientServices,
      });
    }
  }, [currentSalesFunnel, form, isModalOpen]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  const handleSaveModal = useCallback(
    async (values: FunnelModalForm) => {
      const {
        Name,
        Description,
        UseAutoStageSwitch,
        SyncStagesInExistedLeads,
        ClientServices,
      } = values;

      const getServices = () => {
        if (ClientServices?.length === 1 && ClientServices[0] === -1) {
          return [];
        }

        return ClientServices ?? [];
      };

      if (!currentSalesFunnel) {
        showNotification('Воронка продаж не выбрана', FRONT_WARNING);
        return;
      }

      try {
        await updateSalesFunnel({
          id: currentSalesFunnel.Id,
          ActiveBranchId: activeBranchId,
          data: {
            Name: Name ?? '',
            Description: Description ?? '',
            UseAutoStageSwitch,
            SyncStagesInExistedLeads,
            ClientServices: getServices(),
          },
        }).unwrap();

        showNotification('', SUCCESS);

        handleModalClose();
        form.resetFields();
        refetchGetSalesFunnelStages();
      } catch (error) {
        showNotification(error);
      }
    },
    [
      currentSalesFunnel,
      updateSalesFunnel,
      activeBranchId,
      showNotification,
      handleModalClose,
      form,
      refetchGetSalesFunnelStages,
    ]
  );

  return (
    <Modal
      title="Настройки воронки продаж"
      okText={SAVE}
      cancelText={CANCEL}
      open={isModalOpen}
      onCancel={handleModalClose}
      className={`${PREFIX}sales-funnel-settings-modal`}
      onOk={form.submit}
      okButtonProps={{ loading: isModalLoading }}
      cancelButtonProps={{ disabled: isModalLoading }}
    >
      <Form form={form} onFinish={handleSaveModal}>
        <SalesFunnelFormContent form={form} />
      </Form>
    </Modal>
  );
};
